import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import MUIButton from "../../components/MUIButton/MUIButton";
import { useLocation, useNavigate } from "react-router-dom";
import mcqManagementService from "../../services/MCQManagementService";
import { getEntity, removeEntity } from "../../common/CommonFunctions";
import { Icons } from "../../config/MuiIcons.config";
import MUICard from "../../components/MUICard/MUICard.component";
import MUILoader from "../../components/MUILoader/MUILoader";
import ThumbnailImage from "../../components/ThumbnailImage/ThumbnailImage";
import { AppGlobalContext } from "../../contexts/AppContext.context";
import "./MCQManagement.style.css";
import { AppTheme } from "../../theme/App.theme";

const useQuizMeta = () => {
  const location = useLocation();
  const stateMeta = location.state || {};
  const sessionMeta = getEntity("QUIZ_META") || {};
  const meta = { ...sessionMeta, ...stateMeta };
  return meta;
};

const MCQManagement = () => {
  const { authUserDetails } = useContext(AppGlobalContext);
  const navigate = useNavigate();
  const meta = useQuizMeta();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({}); // {questionId: selectedIndex}
  const [locked, setLocked] = useState({}); // {questionId: true}
  const [showResult, setShowResult] = useState(false);

  // Back Confirmation for Quiz
  const [showBackConfirmation, setShowBackConfirmation] = useState(false);

  // Fetch questions by chapterId
  useEffect(() => {
    if (!authUserDetails?.uid) return;
    let active = true;
    (async () => {
      if (!meta?.chapterId) {
        navigate("/educational-topics");
        return;
      }
      try {
        setLoading(true);
        const res = await mcqManagementService.getAllByChapter(meta.chapterId, {
          isActive: true,
        });
        const data =
          (res?.data?.questions || res?.data || res || []).questions ||
          res?.data?.questions ||
          [];
        const list = Array.isArray(data) ? data : [];
        if (active) setQuestions(list);
      } catch (_) {
        if (active) setQuestions([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [meta?.chapterId, authUserDetails]);

  const total = questions.length;
  const current = questions[currentIndex] || {};

  const isAnswered = locked[current.id];
  const selectedIdx = answers[current.id];

  const handleSelect = (idx) => {
    if (isAnswered) return;
    setAnswers((prev) => ({ ...prev, [current.id]: idx }));
    setLocked((prev) => ({ ...prev, [current.id]: true }));
  };

  const canPrev = currentIndex > 0;
  const canNext = currentIndex < total - 1;

  const correctCount = useMemo(() => {
    return questions.reduce((acc, q) => {
      const i = answers[q.id];
      if (typeof i === "number" && q.options?.[i]?.isCorrect) return acc + 1;
      return acc;
    }, 0);
  }, [questions, answers]);

  const scorePercent = total > 0 ? Math.round((correctCount / total) * 100) : 0;
  const verdict = scorePercent >= 80 ? "Great effort!" : "Keep going!";
  const verdictDesc =
    scorePercent >= 80
      ? "Keep exploring more chapters to strengthen your knowledge."
      : "Review the chapter and try again to improve your score.";

  const retake = () => {
    setAnswers({});
    setLocked({});
    setCurrentIndex(0);
    setShowResult(false);
  };

  const answeredCount = useMemo(() => Object.keys(locked).length, [locked]);

  const goToFirstUnanswered = () => {
    const idx = questions.findIndex((q) => !locked[q.id]);
    if (idx >= 0) setCurrentIndex(idx);
  };

  const ScoreRing = ({ percent, color = "#5C8E00" }) => {
    const angle = Math.min(360, Math.max(0, (percent / 100) * 360));
    const ringStyle = {
      background: `conic-gradient(${color} 0deg ${angle}deg, #e5e7eb ${angle}deg 360deg)`,
    };
    const ringColor = percent >= 80 ? "#5C8E00" : "#E9A100";
    ringStyle.background = `conic-gradient(${ringColor} 0deg ${angle}deg, #e5e7eb ${angle}deg 360deg)`;
    return (
      <div className="mcq-ring" style={ringStyle}>
        <div className="mcq-ring-inner">{percent}%</div>
      </div>
    );
  };

  return (
    <Box className="container" maxWidth={"md"}>
      <Dialog
        open={showBackConfirmation}
        disableEscapeKeyDown
        onClose={(e, reason) => {
          if (reason === "backdropClick") {
            return;
          }
          setShowBackConfirmation(false);
        }}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              maxWidth: "386px",
            },
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pt: 2 }}>
          <Box
            sx={{
              width: 60,
              height: 60,
              mx: "auto",
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
            }}
          >
            <Icons.Warning
              sx={{
                color: AppTheme.PRIMARY_COLOR,
                backgroundColor: AppTheme.PRIMARY_WHITE,
                fontSize: 45,
              }}
            />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", pb: 4 }}>
          <Typography sx={{ fontWeight: 700, mb: 1, fontSize: "22px" }}>
            Are you sure you want to leave the quiz?
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2 }}
          >
            You will lose your progress and need to start over.
          </Typography>
          <Box
            className="d-flex mt-2"
            style={{ gap: 8, flexWrap: "wrap", justifyContent: "center" }}
          >
            <MUIButton
              primary
              sx={{ minWidth: "120px" }}
              variant={"outlined"}
              onClick={() => {
                setShowBackConfirmation(false);
              }}
              children={"Continue"}
            />
            <MUIButton
              primary
              sx={{ minWidth: "120px" }}
              onClick={() => {
                removeEntity("QUIZ_META");
                navigate(-1);
                setShowBackConfirmation(false);
              }}
              children={"Leave Quiz"}
            />
          </Box>
        </DialogContent>
      </Dialog>
      <Box className="mcq-topbar">
        <MUIButton
          primary
          variant={"outlined"}
          startIcon={<Icons.KeyboardBackspaceIcon />}
          onClick={() => {
            if (total === 0 || showResult) {
              removeEntity("QUIZ_META");
              navigate(-1);
            } else {
              setShowBackConfirmation(true);
            }
          }}
          children={"Back"}
          size={"small"}
        />
        <div className="mcq-topbar-spacer" />
        {total > 0 && (
          <Typography variant="body1">
            Question {currentIndex + 1} of {total}
          </Typography>
        )}
      </Box>

      {/* Loader / Fallback */}
      {loading && questions.length === 0 && <MUILoader />}
      {!loading && total === 0 && (
        <MUICard className="mb-3">
          <Typography variant="h6" className="mb-2">
            No questions available
          </Typography>
          <Typography variant="body2" color="text.secondary" className="mb-2">
            We don’t have quiz questions for this chapter yet.
          </Typography>
          <MUIButton
            primary
            variant="outlined"
            onClick={() => {
              removeEntity("QUIZ_META");
              navigate("/educational-topics");
            }}
            children={"Back to Topics"}
          />
        </MUICard>
      )}

      {!showResult && total > 0 && (
        <MUICard className="mb-3">
          {/* In-card Topic/Chapter header */}
          <div className="mcq-header">
            <Typography
              className="mcq-topic-title"
              variant="subtitle2"
              color="text.secondary"
            >
              {meta.topicTitle}
            </Typography>
            <div className="mcq-chapter-row">
              {meta?.chapterThumbnail && (
                <ThumbnailImage
                  src={meta.chapterThumbnail}
                  alt={meta.chapterTitle}
                  size={50}
                  borderRadius={8}
                  style={{
                    minWidth: "50px",
                    minHeight: "50px",
                  }}
                />
              )}
              <Typography className="mcq-chapter-title" variant="subtitle1">
                {meta.chapterTitle}
              </Typography>
            </div>
          </div>

          <Typography className="mb-2 question-text">
            {current.questionText}
          </Typography>
          <RadioGroup
            value={typeof selectedIdx === "number" ? selectedIdx : ""}
            onChange={(e) => handleSelect(Number(e.target.value))}
          >
            {current.options?.map((opt, idx) => {
              const isSelected = selectedIdx === idx;
              const isCorrect = opt.isCorrect;
              const answered = isAnswered;
              const showCorrect = answered && isCorrect; // highlight correct option when answered
              const red = "#ef4444";
              const green = "#22c55e";
              const border = answered
                ? showCorrect
                  ? `2px solid ${green}`
                  : isSelected && !isCorrect
                  ? `2px solid ${red}`
                  : "1px solid #e5e7eb"
                : "1px solid #e5e7eb";
              return (
                <Box key={idx} className={`mb-2 mcq-option`} style={{ border }}>
                  <FormControlLabel
                    value={idx}
                    control={<Radio disabled={answered} />}
                    label={<Typography>{opt.text}</Typography>}
                    style={{ width: "100%", margin: 0, padding: 12 }}
                  />
                </Box>
              );
            })}
          </RadioGroup>

          {isAnswered && current.explanation && (
            <Box className="mcq-explanation mt-2">
              {typeof selectedIdx === "number" &&
                !current.options?.[selectedIdx]?.isCorrect && (
                  <Typography variant="body2" className="mb-1">
                    The correct answer is:{" "}
                    {current.options?.find((o) => o.isCorrect)?.text}
                  </Typography>
                )}
              {current.explanation && (
                <Typography variant="body2" color="text.secondary">
                  {current.explanation}
                </Typography>
              )}
            </Box>
          )}

          <Box className="mcq-toolbar mt-3">
            <div className="mcq-topbar-spacer" />
            {canPrev && (
              <MUIButton
                primary
                variant="outlined"
                onClick={() => setCurrentIndex((i) => i - 1)}
                children={"Previous"}
              />
            )}
            {canNext ? (
              <MUIButton
                primary
                onClick={() => setCurrentIndex((i) => i + 1)}
                children={"Next Question"}
              />
            ) : (
              <MUIButton
                primary
                onClick={() => {
                  if (answeredCount !== total) {
                    goToFirstUnanswered();
                    return;
                  }
                  setShowResult(true);
                }}
                children={"Finish"}
                variant={answeredCount === total ? "contained" : "outlined"}
              />
            )}
          </Box>
        </MUICard>
      )}

      {showResult && (
        <MUICard id="quiz-result" className="mcq-result-card">
          <div className="mcq-result-header mcq-header">
            <Typography
              className="mcq-topic-title"
              variant="subtitle2"
              color="text.secondary"
            >
              {meta.topicTitle}
            </Typography>
            <div className="mcq-chapter-row">
              {meta?.chapterThumbnail && (
                <ThumbnailImage
                  src={meta.chapterThumbnail}
                  alt={meta.chapterTitle}
                  size={50}
                  borderRadius={8}
                  style={{
                    minWidth: "50px",
                    minHeight: "50px",
                  }}
                />
              )}
              <Typography
                className="text-start mcq-chapter-title"
                variant="subtitle1"
              >
                {meta.chapterTitle}
              </Typography>
            </div>
          </div>

          <ScoreRing percent={scorePercent} />
          <Typography variant="body2" color="text.secondary" className="mb-2">
            {correctCount}/{total} Correct
          </Typography>
          <Typography variant="h5" className="mb-2">
            {verdict}
          </Typography>
          <Typography variant="body2" color="text.secondary" className="mb-2">
            {verdictDesc}
          </Typography>

          <Typography variant="body2" color="text.secondary" className="mb-3">
            You've completed the quiz
          </Typography>

          <Box
            className="d-flex"
            style={{ gap: 8, flexWrap: "wrap", justifyContent: "center" }}
          >
            <MUIButton primary onClick={retake} children={"Retake Quiz"} />
            <MUIButton
              primary
              variant={"outlined"}
              onClick={() => {
                removeEntity("QUIZ_META");
                navigate("/educational-topics");
              }}
              children={"Back to Topics"}
            />
          </Box>
        </MUICard>
      )}
    </Box>
  );
};

export default MCQManagement;
